import { useContext, useEffect } from "react"
import { Color, Object3D, Object3DEventMap, Vector2 } from "three"
import { FindMaterialApprox, FindObject } from "../../Common/Utils/ThreeTools"
import ModelElements from "../ModelElements"
import type { ProductModelProps } from "../ModelViewerProduct"
import ProductTextureContext from "../_contexts/ProductTextureContext"
import useGLB, { HasStandardMaterial } from "../_hooks/useGLB"

export default function DadCapWithPrintedPatch(props: ProductModelProps) {
    const url = new URL('@resources/3d-models/glb/dad-cap-with-printed-patch.glb', import.meta.url);
    const model = useGLB(url)
    const elements = model.scene.children
    const [productTexture] = useContext(ProductTextureContext)

    // Normals are a bit intense
    useEffect(() => {
        var material = FindMaterialApprox('cap_out' , elements)
        if(material) {
            material.normalScale = new Vector2(0.2, 0.2)
        }
    }, [elements])

    // Control which patch is displayed
    const patch_types = ["circle_2", "circle_2_5", "oval_2_75", "oval_3_5", "rectangle_2_75", "rectangle_3_5", "square_2", "square_2_5"]
    useEffect(() => {
        for(let patch_type in patch_types) {
            let patch_object = FindObject(patch_types[patch_type], elements, false)
            if (!patch_object) return
            patch_object.visible = patch_types[patch_type] == props.variant
        }
    }, [props.variant, elements])

    const option_color = props.options.get('color') ?? 'black'
    const color = {
        "white": {"cap": new Color(0xefefed)},
        "black": {"cap": new Color(0x2e2e31)},
        "dark_grey": {"cap": new Color(0x474843)},
        "cranberry": {"cap": new Color(0xcf3340)},
        "khaki": {"cap": new Color(0xc2a273)},
        "pink": {"cap": new Color(0xf8cace)},
        "stone": {"cap": new Color(0xe6d2bb)},
        "navy": {"cap": new Color(0x414a67)},
        "light_blue": {"cap": new Color(0xb0c8de)},
    }[option_color]
    
    useEffect(() => {
        if (!color) return
 
        let material = FindMaterialApprox('cap_out', elements)
        if(material) {
            material.color = color["cap"]
            material.aoMap = material.map
        }

        material = FindMaterialApprox('eyelet', elements)
        if(material) material.color = color["cap"]
    }, [color])

    // Apply lab texture
    useEffect(() => {
        let patch_object = FindObject(props.variant, elements, false)
        if (!patch_object) return

        const designMaterial = FindMaterialApprox('design', patch_object.children as Object3D<Object3DEventMap>[] & HasStandardMaterial[])
        
        if (designMaterial) {
            designMaterial.map = productTexture
            designMaterial.map.flipY = false
            designMaterial.map.needsUpdate = true
        }
    }, [productTexture, elements, props.variant])
    
    return <ModelElements scene={model.scene}/>
}
